import { useState, useEffect, useRef } from 'react';
import { hexToRgba, resolveImage, buildSrcSet } from '../utils';

const extractVideoId = (url) => {
    if (!url) return null;
    const ytMatch = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]+)/);
    if (ytMatch) return { type: 'youtube', id: ytMatch[1] };
    const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
    if (vimeoMatch) return { type: 'vimeo', id: vimeoMatch[1] };
    return null;
};

/**
 * Resolves media data for an item based on global media_mode.
 * Call once per item: const resolved = resolveItemMedia(item, mediaMode, mediaFolders);
 */
export function resolveItemMedia(item, mediaMode = 'image', mediaFolders = {}) {
    let resolvedImage = null;
    let resolvedFile = null;
    const folderId = item.image_folder_id;
    const fileId = item.image_file_id;
    if (folderId && fileId && mediaFolders[folderId]) {
        const found = mediaFolders[folderId].find(f => String(f.id) === String(fileId));
        if (found) { resolvedImage = found.image_url; resolvedFile = found; }
    }
    if (!resolvedImage) resolvedImage = item.image_url || null;
    let carouselImages = null;
    if (mediaMode === 'carousel' && item.folder_id && mediaFolders[item.folder_id]) {
        const allFiles = mediaFolders[item.folder_id];
        const selected = item.selected_file_ids;
        carouselImages = selected && selected.length > 0
            ? selected.map(id => allFiles.find(f => String(f.id) === String(id))).filter(Boolean)
            : allFiles;
    }
    // Video: YouTube/Vimeo embed OR self-hosted file
    let video = extractVideoId(item.video_url);
    if (!video && item.video_source === 'file' && item.video_file_id) {
        const fileUrl = resolveImage(item, 'video', mediaFolders) || null;
        if (fileUrl) video = { type: 'file', url: fileUrl };
    }
    return { resolvedImage, resolvedFile, carouselImages, video };
}

/**
 * Mini image carousel with autoplay, arrows, dots.
 */
export function ImageCarousel({ images, title, interval = 4000, fitClass = 'object-cover', aspect = 'aspect-[4/3]', rounded = 'rounded-2xl', className = '' }) {
    const [idx, setIdx] = useState(0);
    const timerRef = useRef(null);
    if (!images || images.length === 0) return null;

    const resetTimer = () => {
        if (timerRef.current) clearInterval(timerRef.current);
        if (images.length > 1) {
            timerRef.current = setInterval(() => setIdx(i => i >= images.length - 1 ? 0 : i + 1), interval);
        }
    };
    useEffect(() => { resetTimer(); return () => clearInterval(timerRef.current); }, [images.length, interval]);

    const prev = () => { setIdx(i => i <= 0 ? images.length - 1 : i - 1); resetTimer(); };
    const next = () => { setIdx(i => i >= images.length - 1 ? 0 : i + 1); resetTimer(); };

    return (
        <div className={`relative w-full ${aspect} ${rounded} overflow-hidden shadow-xl group ${className}`}>
            <img src={images[idx].image_url} alt={title || ''}
                srcSet={buildSrcSet(images[idx])}
                sizes="100vw"
                decoding="async"
                className={`w-full h-full ${fitClass} transition-opacity duration-500`} loading="lazy" />
            {images.length > 1 && (<>
                <button onClick={prev} className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/40 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-black/60">
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" /></svg>
                </button>
                <button onClick={next} className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/40 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-black/60">
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
                </button>
                <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                    {images.map((_, i) => (
                        <button key={i} onClick={() => { setIdx(i); resetTimer(); }}
                            className={`w-2 h-2 rounded-full transition-all ${i === idx ? 'bg-white scale-125' : 'bg-white/50'}`} />
                    ))}
                </div>
            </>)}
        </div>
    );
}

/**
 * Renders media for an item: video > carousel > single image > placeholder.
 * Props: item (with resolvedImage, carouselImages, video from resolveItemMedia),
 *        accent color, fitClass, aspect, interval, rounded.
 */
export default function MediaRenderer({ item, accent = '#6366f1', fitClass = 'object-cover', aspect = 'aspect-[4/3]', interval = 4000, rounded = 'rounded-2xl', className = '' }) {
    if (item.video) {
        if (item.video.type === 'file') {
            return (
                <div className={`relative w-full ${aspect} ${rounded} shadow-xl overflow-hidden ${className}`}>
                    <video src={item.video.url} className={`absolute inset-0 w-full h-full ${fitClass}`} autoPlay muted loop playsInline />
                </div>
            );
        }
        const src = item.video.type === 'youtube'
            ? `https://www.youtube-nocookie.com/embed/${item.video.id}?rel=0`
            : `https://player.vimeo.com/video/${item.video.id}`;
        return (
            <div className={`relative w-full ${aspect} ${rounded} shadow-xl overflow-hidden ${className}`}>
                <iframe src={src} className="absolute inset-0 w-full h-full" frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen loading="lazy" />
            </div>
        );
    }
    if (item.carouselImages && item.carouselImages.length > 0) {
        return <ImageCarousel images={item.carouselImages} title={item.title} interval={interval} fitClass={fitClass} aspect={aspect} rounded={rounded} className={className} />;
    }
    if (item.resolvedImage) {
        return <img src={item.resolvedImage} alt={item.title || ''}
            srcSet={buildSrcSet(item.resolvedFile)}
            sizes="100vw"
            decoding="async"
            className={`w-full ${aspect} ${fitClass} ${rounded} shadow-xl ${className}`} loading="lazy" />;
    }
    // Placeholder
    return (
        <div className={`w-full ${aspect} ${rounded} flex items-center justify-center ${className}`}
            style={{ background: `linear-gradient(135deg, ${hexToRgba(accent, 0.15)}, ${hexToRgba(accent, 0.05)})` }}>
            <svg className="w-12 h-12 opacity-20" style={{ color: accent }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
        </div>
    );
}
